import { useState, useCallback, useRef } from 'react'
import { toLocalImageUrl } from '@/lib/api'

export type Phase = 'idle' | 'generating' | 'done' | 'error'

export interface UseSSEGenerateResult<T> {
  start: (opts: { url: string; buildBody: () => BodyInit }) => void
  abort: () => void
  reset: () => void
  phase: Phase
  events: T[]
  error: string | null
}

export function useSSEGenerate<T extends { type: string } = { type: string }>(): UseSSEGenerateResult<T> {
  const [phase, setPhase] = useState<Phase>('idle')
  const [events, setEvents] = useState<T[]>([])
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const start = useCallback((opts: { url: string; buildBody: () => BodyInit }) => {
    abortRef.current?.abort()
    setPhase('generating')
    setEvents([])
    setError(null)

    const ctrl = new AbortController()
    abortRef.current = ctrl

    fetch(opts.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: opts.buildBody(),
      signal: ctrl.signal,
    }).then(async (res) => {
      const reader = res.body?.getReader()
      if (!reader) return
      const decoder = new TextDecoder()
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const raw = JSON.parse(line.slice(6))
              // SSE 이벤트 내 이미지 URL도 상대경로로 변환
              if (raw.url && typeof raw.url === 'string') raw.url = toLocalImageUrl(raw.url)
              if (raw.imageUrl && typeof raw.imageUrl === 'string') raw.imageUrl = toLocalImageUrl(raw.imageUrl)
              if (Array.isArray(raw.urls)) raw.urls = raw.urls.map((u: string) => toLocalImageUrl(u))
              const event = raw as T
              setEvents(prev => [...prev, event])
              if (event.type === 'done') setPhase('done')
              if (event.type === 'error') {
                setError((event as any).error || 'Error')
                setPhase('error')
              }
            } catch {}
          }
        }
      }
    }).catch((e) => {
      if (e.name !== 'AbortError') {
        setError(e.message)
        setPhase('error')
      }
    })
  }, [])

  return {
    start, phase, events, error,
    abort: () => abortRef.current?.abort(),
    reset: () => { abortRef.current?.abort(); setPhase('idle'); setEvents([]); setError(null) },
  }
}
